import BackButton from './BackButton';

/**
 * The one content column every player lesson detail screen uses.
 *
 * Before this, each screen picked its own: BundleProgress ran full-bleed in a
 * panel and 760 as a page, WaitingForLesson 860 in a panel and 760 as a page,
 * and which one you got depended on how you arrived — Lessons.tsx passes
 * `detailStack.length === 1 ? 'panel' : 'page'`, so the SAME screen was one
 * width when selected from the list and another when pushed from inside
 * another detail. Measured in a 1082 pane: 1018, 796 and 696. Switching
 * between them, nothing lined up.
 *
 * Centred at 760, giving a 696 content column. A left-aligned 960 was tried
 * and reverted: it reclaimed the gutters but the measure that bought was not
 * worth the loss of a centred column.
 *
 * 760 is the value the page variant of all three already declared, so this is
 * the number the design had converged on; the panel's 860 and the full-bleed
 * case are what diverged from it.
 *
 * Horizontal only. Vertical padding stays with each screen, because their
 * rhythms are already correct and matching them was not the ask.
 *
 * Prose still caps itself where it lives — see the status copy in
 * WaitingForLesson — rather than relying on the column to hold the measure.
 * That cap survives this revert and is inert at anything under 520.
 */
export const DETAIL_COLUMN = 'w-full mx-auto max-w-[760px] px-6 md:px-8';

/**
 * Title row. The back button is mobile-only.
 *
 * It briefly lived in the gutter beside a centred column, which stopped being
 * a place once the column moved left. On desktop it is gone rather than
 * relocated: both hosts already carry their own way back — the Lessons list
 * re-selects the program and lands on its overview (selectDetail REPLACES the
 * stack, so it returns to the overview rather than to the lesson), and
 * LessonOrderPanel has a back button in its own 60px header whenever a detail
 * is open. Mobile has neither, so it keeps its button.
 *
 * With the button gone from desktop, the title starts at the column's left
 * edge on every screen. On mobile all of these render as pages and all carry
 * the button, so they displace identically and still agree with each other.
 */
export function DetailTitleRow({
  title, onBack, sticky = false, backFallback = '/lessons',
}: {
  title: string;
  /** Omitted by the panel variants, where the list beside them is the way back. */
  onBack?: () => void;
  sticky?: boolean;
  backFallback?: string;
}) {
  return (
    <div className={`relative flex items-center gap-3 py-2 ${sticky ? 'sticky top-0 z-30 bg-db-bg' : ''}`}>
      {onBack && (
        <div className="flex-shrink-0 md:hidden">
          <BackButton onClick={onBack} fallback={backFallback} flush />
        </div>
      )}
      <h1 className="text-[26px] font-bold text-[#1C1C1C]">{title}</h1>
    </div>
  );
}
